// Modelo y reglas de la tabla de Tiendas: filas, alertas, píldoras, filtros y orden. Puro.
import type { StoreActivityRow } from '@/api/services/retail-org/backoffice';
import type { StoresTableKind, StoresTableRow } from '@/api/services/retail-wizard/backoffice';
import type { RetailUser, Store } from '@/api/services/retail-org/retail-org.client';
import { RetailUserStatusEnum } from '@fiado/type-kit/bin/retailOrg/enums/RetailUserStatusEnum';
import { StoreStatusEnum } from '@fiado/type-kit/bin/retailOrg/enums/StoreStatusEnum';
import { daysBetweenMx } from '@/lib/dateHelpers';
import { isSeller } from '../../lib/roster';

/** La fila que pinta la pantalla. `null` es «no hay fuente» y se pinta «—». */
export interface StoreRow {
    storeId: string;
    code: string | null;
    name: string;
    zoneName: string | null;
    /** Estatus del padrón; `null` si la tienda solo aparece en ventas. */
    status: StoreStatusEnum | null;
    kind: StoresTableKind | null;
    salesCount: number | null;
    placedCents: number | null;
    averageTicketCents: number | null;
    lastSaleAt: string | null;
    /** Colocación de los últimos 7 días contra los 7 anteriores, en porcentaje. */
    weekOverWeekPct: number | null;
    /** Vendió esta semana y la anterior no: el mock lo rotula «nuevo». */
    weekIsNew: boolean;
    /** Meta del periodo en centavos; `null` sin meta. */
    goalCents: number | null;
    /** Vendedores activos del padrón con esta tienda como casa; `null` si el padrón no respondió. */
    activeSellers: number | null;
    /** Vendedores del padrón con esta tienda como casa, activos o no. */
    teamTotalCount: number | null;
    managerId: string | null;
    managerName: string | null;
    /** Clientes distintos que compraron en el periodo; `null` sin fuente. */
    customersServed: number | null;
    /** Participación en la colocación de la cadena, en porcentaje; `null` la calcula la pantalla. */
    networkSharePct: number | null;
    npiM30Pct: number | null;
}

export const UNNAMED_STORE = 'Sin nombre';

export function storeKindLabel(kind: StoresTableKind): string {
    return kind === 'OWN' ? 'Propia' : 'Subdistribuidor';
}

const STORE_STATUSES: ReadonlySet<string> = new Set(Object.values(StoreStatusEnum));

/** El estatus del wire como enum; un valor desconocido se trata como sin dato. */
export function toStoreStatus(status: string | null | undefined): StoreStatusEnum | null {
    return status && STORE_STATUSES.has(status) ? (status as StoreStatusEnum) : null;
}

/** La fila de `GET /backoffice/stores/table`, ya cruzada por el BFF. */
export function fromTableRow(row: StoresTableRow): StoreRow {
    return {
        storeId: row.storeId,
        code: row.code === '' ? null : row.code,
        name: row.name ?? UNNAMED_STORE,
        zoneName: row.zoneName,
        status: toStoreStatus(row.status),
        kind: row.kind,
        salesCount: row.salesCount,
        placedCents: row.placedCents,
        averageTicketCents: row.averageTicketCents,
        lastSaleAt: row.lastSaleAt,
        weekOverWeekPct: row.weekOverWeekChangePct,
        weekIsNew: false,
        goalCents: row.goalCents,
        activeSellers: row.teamActiveCount,
        teamTotalCount: row.teamTotalCount,
        managerId: row.managerRetailUserId,
        managerName: row.managerRetailUserId === null ? null : (row.managerName ?? UNNAMED_STORE),
        customersServed: row.customersServed,
        networkSharePct: row.networkSharePct,
        npiM30Pct: row.npiM30Pct,
    };
}

/** Umbrales del mock: más de 3 días sin venta y caída mayor a 20% vs semana anterior. */
export const NO_SALE_DAYS_THRESHOLD = 3;
export const DROP_THRESHOLD_PCT = -20;
const TOP_NETWORK_FRACTION = 0.1;
const TOP_PILL_FRACTION = 0.25;

export interface StoreRowsInput {
    stores: readonly Store[];
    zoneNames: ReadonlyMap<string, string>;
    period: readonly StoreActivityRow[];
    /** Ventana de los últimos 7 días; `undefined` mientras no responde. */
    week: readonly StoreActivityRow[] | undefined;
    /** Los 7 días anteriores; sirve para la tienda que esta semana no vendió. */
    previousWeek: readonly StoreActivityRow[] | undefined;
    users: readonly RetailUser[] | undefined;
    /** Meta del periodo por tienda, ya escalada. */
    goalByStore: ReadonlyMap<string, number>;
}

function weekTrend(
    storeId: string,
    week: readonly StoreActivityRow[] | undefined,
    previousWeek: readonly StoreActivityRow[] | undefined,
): { pct: number | null; isNew: boolean } {
    if (week === undefined) return { pct: null, isNew: false };
    const current = week.find((row) => row.storeId === storeId);
    if (current) {
        if (current.weekOverWeekChangePct !== null) {
            return { pct: current.weekOverWeekChangePct, isNew: false };
        }
        const isNew = (current.placedCents ?? 0) > 0 && (current.baselineCents ?? 0) === 0;
        return { pct: null, isNew };
    }
    const before = previousWeek?.find((row) => row.storeId === storeId);
    return { pct: (before?.placedCents ?? 0) > 0 ? -100 : null, isNew: false };
}

/** Tiendas del padrón (activas, o con venta en el periodo) cruzadas con su actividad y su equipo. */
export function buildStoreRows(input: StoreRowsInput): StoreRow[] {
    const activityById = new Map(input.period.map((row) => [row.storeId, row]));
    const userById = new Map((input.users ?? []).map((user) => [user.retailUserId, user]));
    const activeByStore = new Map<string, number>();
    const totalByStore = new Map<string, number>();
    for (const user of input.users ?? []) {
        if (!isSeller(user)) continue;
        totalByStore.set(user.homeStoreId, (totalByStore.get(user.homeStoreId) ?? 0) + 1);
        if (user.status !== RetailUserStatusEnum.ACTIVE) continue;
        activeByStore.set(user.homeStoreId, (activeByStore.get(user.homeStoreId) ?? 0) + 1);
    }

    const toRow = (
        storeId: string,
        activity: StoreActivityRow | undefined,
        store: Store | undefined,
    ): StoreRow => {
        const trend = weekTrend(storeId, input.week, input.previousWeek);
        const managerId = store?.managerRetailUserId ?? null;
        const manager = managerId === null ? undefined : userById.get(managerId);
        const rosterZone =
            store?.zoneId == null ? null : (input.zoneNames.get(store.zoneId) ?? null);
        return {
            storeId,
            code: store?.code ?? null,
            name: store?.name ?? activity?.storeName ?? storeId,
            zoneName: activity?.zoneName ?? rosterZone,
            status: store?.status ?? null,
            kind: null,
            salesCount: activity ? activity.salesCount : 0,
            placedCents: activity ? activity.placedCents : 0,
            averageTicketCents: activity?.averageTicketCents ?? null,
            lastSaleAt: activity?.lastSaleAt ?? null,
            weekOverWeekPct: trend.pct,
            weekIsNew: trend.isNew,
            goalCents: input.goalByStore.get(storeId) ?? null,
            activeSellers: input.users === undefined ? null : (activeByStore.get(storeId) ?? 0),
            teamTotalCount: input.users === undefined ? null : (totalByStore.get(storeId) ?? 0),
            managerId,
            managerName: manager ? (manager.displayName ?? null) : null,
            customersServed: null,
            networkSharePct: null,
            npiM30Pct: null,
        };
    };

    const rows: StoreRow[] = [];
    for (const store of input.stores) {
        const activity = activityById.get(store.storeId);
        activityById.delete(store.storeId);
        if (!activity && store.status !== StoreStatusEnum.ACTIVE) continue;
        rows.push(toRow(store.storeId, activity, store));
    }
    // Una venta de una tienda que el padrón ya no lista se conserva: su dinero no se esconde.
    for (const activity of activityById.values()) {
        rows.push(toRow(activity.storeId, activity, undefined));
    }
    return rows;
}

export function hasStoreSales(row: StoreRow): boolean {
    return (row.salesCount ?? 0) > 0 || (row.placedCents ?? 0) > 0;
}

/** Suspendida o dada de baja en el padrón; como en Vendedores, solo lleva pastilla. */
export function isInactiveStore(row: Pick<StoreRow, 'status'>): boolean {
    return row.status === StoreStatusEnum.SUSPENDED || row.status === StoreStatusEnum.INACTIVE;
}

export type StoreStatusTab = 'ACTIVE' | 'SUSPENDED' | 'INACTIVE' | 'ALL';

export const DEFAULT_STORE_STATUS_TAB: StoreStatusTab = 'ACTIVE';

/** Pestaña de la fila; sin estatus del padrón cuenta como activa para no esconder su venta. */
function statusTabOf(row: StoreRow): Exclude<StoreStatusTab, 'ALL'> {
    return row.status ?? StoreStatusEnum.ACTIVE;
}

export function filterStoresByStatus(rows: readonly StoreRow[], tab: StoreStatusTab): StoreRow[] {
    return tab === 'ALL' ? [...rows] : rows.filter((row) => statusTabOf(row) === tab);
}

export function storeStatusCounts(rows: readonly StoreRow[]): Record<StoreStatusTab, number> {
    const counts: Record<StoreStatusTab, number> = {
        ACTIVE: 0,
        SUSPENDED: 0,
        INACTIVE: 0,
        ALL: rows.length,
    };
    for (const row of rows) counts[statusTabOf(row)] += 1;
    return counts;
}

/** Quienes colocaron algo, de mayor a menor colocación. */
function placedRanking(rows: readonly StoreRow[]): StoreRow[] {
    return rows
        .filter((row) => (row.placedCents ?? 0) > 0)
        .sort((a, b) => (b.placedCents ?? 0) - (a.placedCents ?? 0));
}

function topIds(ranked: readonly StoreRow[], fraction: number): Set<string> {
    return new Set(ranked.slice(0, Math.ceil(ranked.length * fraction)).map((row) => row.storeId));
}

export interface StoreContext {
    /** Lugar en la red por colocación (1 = la mejor); sin colocación no tiene lugar. */
    ranks: Map<string, number>;
    rankedCount: number;
    top10: Set<string>;
    top25: Set<string>;
    totalPlacedCents: number;
    now: Date;
}

export function buildStoreContext(rows: readonly StoreRow[], now: Date = new Date()): StoreContext {
    const ranked = placedRanking(rows);
    return {
        ranks: new Map(ranked.map((row, index) => [row.storeId, index + 1])),
        rankedCount: ranked.length,
        top10: topIds(ranked, TOP_NETWORK_FRACTION),
        top25: topIds(ranked, TOP_PILL_FRACTION),
        totalPlacedCents: rows.reduce((sum, row) => sum + (row.placedCents ?? 0), 0),
        now,
    };
}

/** Días desde la última venta; `null` si no se sabe. */
export function daysWithoutSale(row: StoreRow, now: Date = new Date()): number | null {
    return row.lastSaleAt === null ? null : daysBetweenMx(row.lastSaleAt, now);
}

export interface StoreAlerts {
    topNetwork: boolean;
    /** Sin venta reciente: los días si se saben, o `null` si no vendió en todo el periodo. */
    noSale: { days: number | null } | null;
    drop: boolean;
}

export function storeAlerts(row: StoreRow, context: StoreContext): StoreAlerts {
    const days = daysWithoutSale(row, context.now);
    let noSale: StoreAlerts['noSale'] = null;
    if (days !== null && days > NO_SALE_DAYS_THRESHOLD) noSale = { days };
    else if (days === null && !hasStoreSales(row)) noSale = { days: null };
    return {
        topNetwork: context.top10.has(row.storeId),
        noSale,
        drop: row.weekOverWeekPct !== null && row.weekOverWeekPct < DROP_THRESHOLD_PCT,
    };
}

export function needsAttention(alerts: StoreAlerts): boolean {
    return alerts.noSale !== null || alerts.drop;
}

/** Peso para ordenar la columna de alertas: sin venta > caída > top. */
function alertWeight(alerts: StoreAlerts): number {
    if (alerts.noSale !== null) return 3;
    if (alerts.drop) return 2;
    if (alerts.topNetwork) return 1;
    return 0;
}

/** Cumplimiento contra la meta en pesos, como fracción; `null` sin meta. */
export function storeGoalProgress(row: StoreRow): number | null {
    if (row.goalCents === null || row.goalCents <= 0 || row.placedCents === null) return null;
    return row.placedCents / row.goalCents;
}

/** Fracción de la colocación del tenant; `null` si nadie colocó. */
export function tenantShare(row: StoreRow, context: StoreContext): number | null {
    if (row.networkSharePct !== null) return row.networkSharePct / 100;
    if (context.totalPlacedCents <= 0) return null;
    return (row.placedCents ?? 0) / context.totalPlacedCents;
}

export type StorePill = 'all' | 'withSales' | 'attention' | 'top' | 'noTeam';

export interface StoresFilters {
    query: string;
    pill: StorePill;
}

export const EMPTY_STORES_FILTERS: StoresFilters = { query: '', pill: 'all' };

/** Filtros activos de la columna; la píldora no cuenta porque vive en la barra de la lista. */
export function countActiveStoreFilters(filters: StoresFilters): number {
    return filters.query.trim() === '' ? 0 : 1;
}

function matchesPill(row: StoreRow, pill: StorePill, context: StoreContext): boolean {
    switch (pill) {
        case 'all':
            return true;
        case 'withSales':
            return hasStoreSales(row);
        case 'attention':
            return needsAttention(storeAlerts(row, context));
        case 'top':
            return context.top25.has(row.storeId);
        case 'noTeam':
            return row.activeSellers === 0;
    }
}

export function filterStoreRows(
    rows: readonly StoreRow[],
    filters: StoresFilters,
    context: StoreContext,
): StoreRow[] {
    const query = filters.query.trim().toLowerCase();
    return rows.filter((row) => {
        if (query) {
            const haystack = `${row.name} ${row.code ?? ''} ${row.zoneName ?? ''}`.toLowerCase();
            if (!haystack.includes(query)) return false;
        }
        return matchesPill(row, filters.pill, context);
    });
}

export function storePillCounts(rows: readonly StoreRow[], context: StoreContext) {
    const count = (pill: StorePill) => rows.filter((row) => matchesPill(row, pill, context)).length;
    return {
        all: rows.length,
        withSales: count('withSales'),
        attention: count('attention'),
        top: count('top'),
        noTeam: count('noTeam'),
    };
}

export type StoreSortKey =
    | 'code'
    | 'name'
    | 'zone'
    | 'kind'
    | 'alerts'
    | 'team'
    | 'placed'
    | 'weekOverWeek'
    | 'sales'
    | 'goal'
    | 'npi'
    | 'share';

export interface StoreSort {
    key: StoreSortKey;
    direction: 'asc' | 'desc';
}

export const DEFAULT_STORE_SORT: StoreSort = { key: 'placed', direction: 'desc' };

const TEXT_SORT_KEYS: ReadonlySet<StoreSortKey> = new Set(['code', 'name', 'zone', 'kind']);

/** Mismo encabezado invierte el sentido; uno nuevo arranca asc en texto y desc en cifras. */
export function toggleStoreSort(current: StoreSort, key: StoreSortKey): StoreSort {
    if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    }
    return { key, direction: TEXT_SORT_KEYS.has(key) ? 'asc' : 'desc' };
}

function sortValue(
    row: StoreRow,
    key: StoreSortKey,
    context: StoreContext,
): string | number | null {
    switch (key) {
        case 'code':
            return row.code;
        case 'name':
            return row.name;
        case 'zone':
            return row.zoneName;
        case 'kind':
            return row.kind;
        case 'alerts':
            return alertWeight(storeAlerts(row, context));
        case 'team':
            return row.activeSellers;
        case 'placed':
            return row.placedCents;
        case 'weekOverWeek':
            return row.weekIsNew ? Number.POSITIVE_INFINITY : row.weekOverWeekPct;
        case 'sales':
            return row.salesCount;
        case 'goal':
            return storeGoalProgress(row);
        case 'npi':
            return row.npiM30Pct;
        case 'share':
            return tenantShare(row, context);
    }
}

/** Ordena por la columna pedida; los `null` siempre al final y el nombre desempata. */
export function sortStoreRows(
    rows: readonly StoreRow[],
    sort: StoreSort,
    context: StoreContext,
): StoreRow[] {
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const av = sortValue(a, sort.key, context);
        const bv = sortValue(b, sort.key, context);
        if (av === null && bv !== null) return 1;
        if (bv === null && av !== null) return -1;
        if (av !== null && bv !== null && av !== bv) {
            const cmp =
                typeof av === 'string' && typeof bv === 'string'
                    ? av.localeCompare(bv, 'es-MX')
                    : Number(av) - Number(bv);
            if (cmp !== 0) return cmp * sign;
        }
        return a.name.localeCompare(b.name, 'es-MX');
    });
}
